<script setup lang="ts">
import { useSurveyDragDropOperations } from "@/composition/builder/useSurveyDragDropOperations";
import { useAutoScroll } from "@/composition/useAutoScroll";
import { MediaCenterSubCategory } from "@/helpers/mediaCenter.type";
import { useAppStore } from "@/store/app";
import { IMAGE_LAYOUT, SURVEY_MODE } from "@/util/constants";
import { getCheckBoxStyles, scopeCustomStyle } from "@/util/default";
import {
  createBuilderScrollPreserver,
  preserveBuilderScrollPosition,
} from "@/util/focusNavigation";
import { getEffectiveBackgroundImageUrlForDevice } from "@/util/formStyleBackgroundImage";
import { localizeLegacyNames } from "@/util/localize";
import { clearBillingWithoutAddress } from "@/util/sellProductAddress";
import {
  addActiveTabLoopListener,
  buildClonedSlideDataAndTasks,
  focusLabelAfterActiveChange,
  focusLabelFromUserEvent,
  getAllFontsImport,
  getElementDOMUniqueId,
  removeActiveTabLoopListener,
  scrollContainerBySlideHeight,
} from "@/util/methods";
import {
  AlertCircleIcon,
  Copy01Icon,
  Settings02Icon,
  Trash04Icon,
} from "@gohighlevel/ghl-icons/24/outline";
import { UITooltip } from "@gohighlevel/ghl-ui";
import { cloneDeep } from "lodash";
import { storeToRefs } from "pinia";
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import { VueDraggableNext } from "vue-draggable-next";
import { useI18n } from "vue-i18n";
import AlertModal from "../common/AlertModal.vue";
import BuilderBranding from "../common/BuilderBranding.vue";
import ContactAssociationSettingsModal from "../common/ContactAssociationSettingsModal.vue";
import CustomFieldsPreview from "../common/CustomFieldsPreview.vue";
import DefaultScreen from "../common/DefaultScreen.vue";
import Divider from "../common/Divider.vue";
import Footer from "../common/Footer.vue";
import HLImage from "../common/HLImage.vue";
import MixedCustomObjectErrorModal from "../common/MixedCustomObjectErrorModal.vue";
import StandardFieldPreview from "../common/StandardFieldPreview.vue";

const { t } = useI18n();
const store = useAppStore();
const builderCanvasBackgroundUrl = computed(() =>
  getEffectiveBackgroundImageUrlForDevice(store.formStyle, store.deviceMode)
);
const { elements } = storeToRefs(store);
const currentElement = ref({ label: "" });
const globalScriptCSSStyle = ref({ textContent: null });
const globalScriptCSSLabelHolder = ref({ textContent: null });
const globalScriptCSSPlaceHolder = ref({ textContent: null });
const globalScriptCSSFieldBGColor = ref({ textContent: null });
const globalScriptCSSFieldBorder = ref({ textContent: null });
const globalScriptCSSFieldShadow = ref({ textContent: null });
const globalScriptCSSFieldWidth = ref({ textContent: null });
const globalScriptCSSFieldPadding = ref({ textContent: null });
const globalScriptCSSFontHolder = ref({ textContent: null });

const globalScriptCSSShortLabelHolder = ref({ textContent: null });
const fullWidthFields = [
  "img",
  "submit",
  "h1",
  "html",
  "captcha",
  "large_text",
  "source",
  "file_upload",
];
const openAlertModal = ref(false);
const slideDeleteTitle = ref(t("common.alert"));
const slideDeleteMessage = ref(t("survey.slideDeleteAlertMessage"));
const slideDeletePositiveText = ref(t("common.ok"));
const slideDeleteNegativeText = ref(t("common.cancel"));
const slideToBeRemoved = ref<number | undefined>(undefined);
const preserveBuilderScroll = createBuilderScrollPreserver();

// Auto-scroll during drag
const { handleDragStart, handleDragEnd } = useAutoScroll("form-builder");

const formWidth = computed(() => {
  return store.deviceMode === "mobile" ? 400 : store.formStyle.width;
});

const progressBarWidth = computed(() => {
  return (slideIndex: number) => {
    const progress =
      slideIndex === store.slides.length - 1
        ? 95
        : ((slideIndex + 1) * 100) / store.slides.length;
    return `${progress}%`;
  };
});

const formStyle = computed(() => {
  const style = store.formStyle;
  // Use mobile padding if in mobile mode and it exists, otherwise fallback to desktop padding
  const padding =
    store.deviceMode === "mobile"
      ? style?.mobilePadding || style?.padding
      : style?.padding;
  const margin =
    store.deviceMode === "mobile"
      ? style?.mobileMargin || style?.margin
      : style?.margin;
  const footerStyle =
    store.deviceMode === "mobile" ? store.mobileFooterStyle : store.footerStyle;

  // Only apply bottom border radius if footer is not sticky
  const borderRadius = footerStyle?.stickyFooter
    ? `${style?.border?.radius}px ${style?.border?.radius}px 0 0`
    : `${style?.border?.radius}px`;
  // Calculate shadow extent (blur + spread)
  const shadowExtent =
    (style?.shadow?.blur || 0) + (style?.shadow?.spread || 0);

  return {
    padding: `${padding?.top || 0}px ${padding?.right || 0}px ${
      padding?.bottom || 0
    }px ${padding?.left || 0}px`,
    backgroundColor: `#${style?.background}`,
    border: `${style?.border?.border}px ${style?.border?.style} #${style?.border?.color}`,
    borderBottom: "none",
    borderRadius: borderRadius,
    boxShadow: `${style?.shadow?.horizontal}px ${style?.shadow?.vertical}px ${style?.shadow?.blur}px ${style?.shadow?.spread}px #${style?.shadow?.color}`,
    width: `${formWidth.value}px`,
    clipPath: `inset(-${shadowExtent}px -${shadowExtent}px 0px -${shadowExtent}px)`,
  };
});

const containerMarginTop = computed(() => {
  const style = store.formStyle;
  const margin =
    store.deviceMode === "mobile"
      ? style?.mobileMargin || style?.margin
      : style?.margin;
  const normalize = (v) =>
    v === "auto" || v === undefined || v === null ? 20 : v;

  return `${normalize(margin?.top)}px`;
});

const containerMarginBottom = computed(() => {
  const style = store.formStyle;
  const margin =
    store.deviceMode === "mobile"
      ? style?.mobileMargin || style?.margin
      : style?.margin;
  const normalize = (v) =>
    v === "auto" || v === undefined || v === null ? 20 : v;

  return `${normalize(margin?.bottom)}px`;
});

const clickedOnElement = async (e, element, index, slideIndex) => {
  await preserveBuilderScrollPosition(async () => {
    e.stopPropagation();
    const alreadyActive =
      store.currentActiveIndex[0] === slideIndex &&
      store.currentActiveIndex[1] === index;

    if (!alreadyActive) {
      store.currentActiveIndex = [slideIndex, index];
      store.currentSlideIndex = slideIndex;
    }

    if (
      !store.sidebarOverlaySettings ||
      store.sidebarOverlaySettings.index !== index ||
      store.sidebarOverlaySettings.slideIndex !== slideIndex
    ) {
      store.sidebarOverlaySettings = {
        index,
        slideIndex,
        formIndex: undefined,
      };
    }

    currentElement.value = element;
    await nextTick();
    if (!alreadyActive) {
      focusLabelFromUserEvent(e);
    }
  });
};

const fieldStyle = computed(() => {
  const fieldStyleStore = store.fieldStyle;
  const style = {
    padding: `${fieldStyleStore?.padding?.top}px ${fieldStyleStore?.padding?.right}px ${fieldStyleStore?.padding?.bottom}px ${fieldStyleStore?.padding?.left}px`,
    backgroundColor: "#" + fieldStyleStore?.bgColor,
    border: `${fieldStyleStore?.border?.border}px ${
      fieldStyleStore?.border?.type === "none"
        ? ""
        : fieldStyleStore?.border?.type
    } #${fieldStyleStore?.border?.color}`,
    borderRadius: fieldStyleStore?.border?.radius + "px",
    boxShadow: `${fieldStyleStore?.shadow?.horizontal}px ${fieldStyleStore?.shadow?.vertical}px ${fieldStyleStore?.shadow?.blur}px ${fieldStyleStore?.shadow?.spread}px #${fieldStyleStore?.shadow?.color}`,
    width: fieldStyleStore?.width + "px",
    color: "#" + fieldStyleStore?.labelColor,
    mobileLabelColor: "#" + fieldStyleStore?.mobileLabelColor,
    font: `${fieldStyleStore.labelFontSize}px  ${fieldStyleStore.labelFontWeight} ${fieldStyleStore.labelFontFamily}`,
  };
  return style;
});

onMounted(() => {
  const globalScriptCSS = document.createElement("style");
  globalScriptCSS.type = "text/css";

  // Google and bunny font css script
  const globalScriptFontFamily = document.createElement("style");
  globalScriptFontFamily.type = "text/css";
  globalScriptFontFamily.appendChild(document.createTextNode(""));
  globalScriptCSSFontHolder.value = globalScriptFontFamily.childNodes[0];
  document.head.appendChild(globalScriptFontFamily);

  for (let i = 0; i < 9; i++) {
    globalScriptCSS.appendChild(document.createTextNode(""));
  }
  globalScriptCSSStyle.value = globalScriptCSS.childNodes[0];
  //For Label CSS Change
  globalScriptCSSLabelHolder.value = globalScriptCSS.childNodes[1];
  //For Placeholder CSS Change
  globalScriptCSSPlaceHolder.value = globalScriptCSS.childNodes[2];
  globalScriptCSSFieldBGColor.value = globalScriptCSS.childNodes[3];
  globalScriptCSSFieldBorder.value = globalScriptCSS.childNodes[4];
  globalScriptCSSFieldShadow.value = globalScriptCSS.childNodes[5];
  globalScriptCSSFieldWidth.value = globalScriptCSS.childNodes[6];
  globalScriptCSSFieldPadding.value = globalScriptCSS.childNodes[7];
  globalScriptCSSShortLabelHolder.value = globalScriptCSS.childNodes[8];

  document.head.appendChild(globalScriptCSS);
  globalScriptCSSFontHolder.value.textContent = getAllFontsImport(
    store.fontCollection,
    store.builderConfig.isGDPRCompliant
  );
  setGlobalStyleInHTMl();
  // Localize any legacy default slide names like "Slide N"
  localizeLegacyNames(store.slides, /^Slide\s+\d+$/i, (n) =>
    t("survey.slideLabel", { n })
  );
  addActiveTabLoopListener();
});

onBeforeUnmount(() => {
  removeActiveTabLoopListener();
});

// When the active element changes (via click, clone, or programmatic),
// try to auto-focus its label
watch(
  () => store.currentActiveIndex,
  async () => {
    await nextTick();
    focusLabelAfterActiveChange();
  },
  { deep: true }
);

// const standardSidebarEnabled = (e) => {
//   const modals = document.getElementsByClassName("n-modal-container");
//   const colorPickers = document.getElementsByClassName("v-binder-follower-container");
//   const borderTypes = document.getElementsByClassName("v-binder-follower-content");
//   const modalByTiny = document.getElementsByClassName("tox-dialog");
//   let isClickedOnModal = false;
//   Array.prototype.forEach.call(modals, (modal) => {
//     if (modal.contains(e.target)) {
//       isClickedOnModal = true;
//     }
//   });
//   let isClickedOnColorPicker = false;
//   Array.prototype.forEach.call(colorPickers, (colorPicker) => {
//     if (colorPicker?.contains(e.target)) {
//       isClickedOnColorPicker = true;
//     }
//   });
//   let isClickedOnBorderType = false;
//   Array.prototype.forEach.call(borderTypes, (borderType) => {
//     if (borderType?.contains(e.target)) {
//       isClickedOnBorderType = true;
//     }
//   });
//   let tinyPopUp = false;
//   Array.prototype.forEach.call(modalByTiny, (tiny) => {
//     if (tiny?.contains(e.target)) {
//       tinyPopUp = true;
//     }
//   });
//   console.log(store.formSidebarElement?.contains(e.target), "FOmr");
//   if (
//     !isClickedOnModal &&
//     !isClickedOnColorPicker &&
//     !isClickedOnBorderType &&
//     !tinyPopUp &&
//     !store.formSidebarElement?.contains(e.target)
//   ) {
//     store.currentCustomSidebarPreview = undefined;
//     store.sidebarOverlaySettings = undefined;
//     store.isAnyElementActive = false;
//     allSlideInactive();
//   }
// };

const setGlobalStyleInHTMl = () => {
  const fieldStyle = store.fieldStyle;
  const formStyle = store.formStyle;
  const isMobile = store.deviceMode === "mobile";

  const getMobileOrDesktop = (mobileVal, desktopVal) =>
    isMobile ? mobileVal || desktopVal : desktopVal;

  const labelColor = getMobileOrDesktop(
    fieldStyle.mobileLabelColor,
    fieldStyle.labelColor
  );
  const labelFontSize = getMobileOrDesktop(
    fieldStyle.mobileLabelFontSize,
    fieldStyle.labelFontSize
  );
  const labelFontWeight = getMobileOrDesktop(
    fieldStyle.mobileLabelFontWeight,
    fieldStyle.labelFontWeight
  );
  const labelFontFamily = getMobileOrDesktop(
    fieldStyle.mobileLabelFontFamily,
    fieldStyle.labelFontFamily
  );

  const placeholderColor = getMobileOrDesktop(
    fieldStyle.mobilePlaceholderColor,
    fieldStyle.placeholderColor
  );
  const placeholderFontFamily = getMobileOrDesktop(
    fieldStyle.mobilePlaceholderFontFamily,
    fieldStyle.placeholderFontFamily
  );
  const placeholderFontSize = getMobileOrDesktop(
    fieldStyle.mobilePlaceholderFontSize,
    fieldStyle.placeholderFontSize
  );
  const placeholderFontWeight = getMobileOrDesktop(
    fieldStyle.mobilePlaceholderFontWeight,
    fieldStyle.placeholderFontWeight
  );

  const shortLabelColor = getMobileOrDesktop(
    fieldStyle.shortLabel?.mobileColor,
    fieldStyle.shortLabel?.color
  );
  const shortLabelFontSize = getMobileOrDesktop(
    fieldStyle.shortLabel?.mobileFontSize,
    fieldStyle.shortLabel?.fontSize
  );
  const shortLabelFontWeight = getMobileOrDesktop(
    fieldStyle.shortLabel?.mobileFontWeight,
    fieldStyle.shortLabel?.fontWeight
  );
  const shortLabelFontFamily = getMobileOrDesktop(
    fieldStyle.shortLabel?.mobileFontFamily,
    fieldStyle.shortLabel?.fontFamily
  );

  globalScriptCSSLabelHolder.value.textContent = `#_builder-form label, #_builder-form .label-input {
      color: #${labelColor};
      font-size: ${labelFontSize}px;
      font-weight: ${labelFontWeight};
      font-family: '${labelFontFamily}';
      line-height: 1.8;
    }
    #_builder-form .tnc {
      font-family: '${labelFontFamily}';
    }
    #_builder-form pre {
      white-space: pre-wrap;
      word-break: break-word;
      max-width: 100%;
    }
    /* Bubble containers should inherit label typography */
    #_builder-form .bubble-container, #_builder-form .bubble-checkbox-container {
      font-family: '${labelFontFamily}';
      font-size: ${labelFontSize}px;
      font-weight: ${labelFontWeight};
    }
    `;

  globalScriptCSSPlaceHolder.value.textContent = `#_builder-form ::placeholder, #_builder-form .form-dropdown a,
    #_builder-form .form-dropdown .dropdown-toggle, #_builder-form .form-placeholder , #_builder-form .currency-code {
      color : #${placeholderColor};
      font-family:'${placeholderFontFamily}';
      font-size:${placeholderFontSize}px;
      font-weight: ${placeholderFontWeight};
        width: 100%;
    }
    /* Bubble labels use placeholder color on label text */
    #_builder-form .bubble-label, #_builder-form .bubble-checkbox-label {
      color: #${placeholderColor} !important;
      background-color: #${fieldStyle.bgColor} !important;
      font-family:'${placeholderFontFamily}' !important;
      font-size:${placeholderFontSize}px !important;
      font-weight:${placeholderFontWeight} !important;
    }`;
  globalScriptCSSFieldBGColor.value.textContent = `#_builder-form .form-control, .form-control:disabled, .form-control[readonly], #_builder-form .form-dropdown, #_builder-form .other-input-control{ background-color : #${fieldStyle.bgColor}}`;
  globalScriptCSSFieldBorder.value.textContent =
    "  #_builder-form .form-control, .form-control:disabled, .form-control[readonly], #_builder-form .form-dropdown , .form-input-border-style { border:" +
    fieldStyle.border.border +
    "px " +
    fieldStyle.border.type +
    " #" +
    fieldStyle.border.color +
    ";border-radius:" +
    fieldStyle.border.radius +
    "px ; }" +
    ` .line-form-style { border-bottom: ${fieldStyle.border.border}px ${fieldStyle.border.type} #${fieldStyle.border.color} !important; background-color: #${fieldStyle.bgColor} !important;}` +
    ` #_builder-form .bubble-container, #_builder-form .bubble-checkbox-container { border: ${fieldStyle.border.border}px ${fieldStyle.border.type} #${fieldStyle.border.color} !important; border-radius: 12px !important; }` +
    `.text-border-color-style { color : #${fieldStyle.border.color} } `;

  globalScriptCSSFieldPadding.value.textContent =
    "  #_builder-form .form-control, .form-control:disabled, .form-control[readonly], #_builder-form .form-dropdown , .input-form-padding-style { padding:" +
    fieldStyle.padding.top +
    "px " +
    fieldStyle.padding.right +
    "px " +
    fieldStyle.padding.bottom +
    "px " +
    fieldStyle.padding.left +
    "px}" +
    ` #_builder-form .bubble-container, #_builder-form .bubble-checkbox-container { padding: ${fieldStyle.padding.top}px ${fieldStyle.padding.right}px ${fieldStyle.padding.bottom}px ${fieldStyle.padding.left}px !important; background-clip: inherit !important; margin-left: 2px !important; margin-right: 2px !important; }`;
  globalScriptCSSFieldShadow.value.textContent =
    "  #_builder-form .form-control, .form-control:disabled, .form-control[readonly], #_builder-form .form-dropdown, #_builder-form .other-input-control { box-shadow:" +
    fieldStyle.shadow.horizontal +
    "px " +
    fieldStyle.shadow.vertical +
    "px " +
    fieldStyle.shadow.blur +
    "px " +
    fieldStyle.shadow.spread +
    "px #" +
    fieldStyle.shadow.color +
    "}" +
    ` #_builder-form .bubble-container, #_builder-form .bubble-checkbox-container { box-shadow: ${fieldStyle.shadow.horizontal}px ${fieldStyle.shadow.vertical}px ${fieldStyle.shadow.blur}px ${fieldStyle.shadow.spread}px #${fieldStyle.shadow.color}; }`;
  globalScriptCSSFieldWidth.value.textContent =
    "  #_builder-form .form-control, .form-control:disabled, .form-control[readonly], #_builder-form .form-dropdown {max-width:" +
    fieldStyle.width +
    "px}";
  globalScriptCSSShortLabelHolder.value.textContent = `#_builder-form .short-label, #_builder-form .short-label::placeholder {
      color: #${shortLabelColor} !important;
      font-size: ${shortLabelFontSize}px !important;
      font-weight: ${shortLabelFontWeight};
      font-family: '${shortLabelFontFamily}';
  }`;
  const styleString = scopeCustomStyle(formStyle.customStyle);
  globalScriptCSSStyle.value.textContent =
    styleString + getCheckBoxStyles(fieldStyle);
};

//Store Subscription to watch on store value change using patch
store.$subscribe(() => {
  setGlobalStyleInHTMl();
});

watch([store.fontCollection, () => store.builderConfig.isGDPRCompliant], () => {
  globalScriptCSSFontHolder.value.textContent = getAllFontsImport(
    store.fontCollection,
    store.builderConfig.isGDPRCompliant
  );
});

const updateElementProperty = (
  property: string,
  value: string,
  elementId: string
) => {
  store.slides.forEach((slide) => {
    slide.slideData.forEach((element) => {
      const matchUuid =
        typeof element.uuid === "string" && element.uuid === elementId;
      const matchHidden =
        typeof element.hiddenFieldQueryKey === "string" &&
        element.hiddenFieldQueryKey === elementId;
      if (matchUuid || matchHidden) {
        (element as any)[property] = value;
      }
    });
  });
};

const updateLabel = (newLabel: string, elementId: string) => {
  updateElementProperty("label", newLabel, elementId);
  store.anyUnsavedChanges = true;
};

const updatePlaceholder = (newPlaceholder: string, elementId: string) => {
  updateElementProperty("placeholder", newPlaceholder, elementId);
  store.anyUnsavedChanges = true;
};

const updateShortLabel = (newShortLabel: string, elementId: string) => {
  updateElementProperty("shortLabel", newShortLabel, elementId);
  store.anyUnsavedChanges = true;
};

const updateLowestRating = (lowestRating: string, elementId: string) => {
  updateElementProperty("lowestRating", lowestRating, elementId);
  store.anyUnsavedChanges = true;
};

const updateHighestRating = (highestRating: string, elementId: string) => {
  updateElementProperty("highestRating", highestRating, elementId);
  store.anyUnsavedChanges = true;
};

const isActiveElement = (index, slideIndex) => {
  return (
    store.currentActiveIndex[0] === slideIndex &&
    store.currentActiveIndex[1] === index
  );
};

const {
  changeLog,
  addSlideBelow,
  beginCanvasDrag,
  endCanvasDrag,
} = useSurveyDragDropOperations();

const handleSurveyDragStart = (event, slideIndex: number) => {
  beginCanvasDrag(slideIndex, event.oldIndex);
  handleDragStart();
};

const handleSurveyDragEnd = () => {
  endCanvasDrag();
  handleDragEnd();
};

const handleSurveyElementChange = async (e, slideIndex) => {
  await preserveBuilderScrollPosition(() => changeLog(e, slideIndex), {
    restoreDelays: [0, 100, 300, 800],
    lockDurationMs: 900,
  });
};

const slideChange = (e) => {
  allSlideInactive();
  store.isAnyElementActive = false;
  store.anyUnsavedChanges = true;
};

const getSuccessorIndex = () => {
  return t("survey.slideLabel", { n: store.slides.length + 1 });
};

const allSlideInactive = () => {
  store.currentSlideIndex = undefined;
  store.currentActiveIndex = [undefined, undefined];
};

const clearSurveySelection = () => {
  store.currentCustomSidebarPreview = undefined;
  store.sidebarOverlaySettings = undefined;
  store.isAnyElementActive = false;
  allSlideInactive();
};

const removeSlide = (slideIndex: number) => {
  slideToBeRemoved.value = slideIndex;
  slideDeleteTitle.value = t("common.alert");
  if (slideIndex < store.slides.length - 1) {
    slideDeleteMessage.value = t("survey.slideDeleteBetweenAlertMessage");
  } else {
    slideDeleteMessage.value = t("survey.slideDeleteAlertMessage");
  }
  slideDeletePositiveText.value = t("common.ok");
  slideDeleteNegativeText.value = t("common.cancel");
  openAlertModal.value = true;
};

const openSlideSettings = (slideIndex) => {
  store.sidebarOverlaySettings = {
    index: undefined,
    slideIndex: slideIndex,
    formIndex: undefined,
  };
  store.isAnyElementActive = true;
};

const deleteConfirmedSlide = (removedSlideIndex: number) => {
  store.slides.splice(removedSlideIndex, 1);
  clearBillingWithoutAddress(store.sellProductAddressSlides);
  store.anyUnsavedChanges = true;
};

const feedback = (modalName, result) => {
  if (result && slideToBeRemoved.value !== undefined) {
    deleteConfirmedSlide(slideToBeRemoved.value);
  }
  slideToBeRemoved.value = undefined;
  openAlertModal.value = false;
};
const popModalChange = () => {
  openAlertModal.value = false;
};

// Inline actions now handled directly by icon clicks next to slide name

const clickedOutSideSlide = (e) => {
  clearSurveySelection();
};

const cloneSlide = (slideIndex: number) => {
  const sourceSlide = store.slides[slideIndex];
  if (!sourceSlide) {
    return;
  }

  // Only clone elements whose tag is allowed to be duplicated
  const allowedTags = Array.isArray(store.duplicateAllowed)
    ? store.duplicateAllowed
    : [];
  const filteredSlide = {
    ...sourceSlide,
    // Keep only cloneable elements:
    // - Standard fields that are allowed
    // - Custom fields with model === contact or no model
    slideData: (sourceSlide.slideData || []).filter((el: any) => {
      const isCustom = el?.id !== undefined || el?.custom === true;
      if (isCustom) {
        return !el?.model || el?.model === "contact";
      }
      return allowedTags.includes(el?.tag);
    }),
  } as any;

  const { clonedSlideData, runCustomFieldCreations } =
    buildClonedSlideDataAndTasks(filteredSlide, slideIndex, store);

  const newSlide = {
    ...cloneDeep(sourceSlide),
    id: new Date().getTime() + "-" + store.slides.length,
    slideName: `${sourceSlide.slideName} (copy)`,
    slideData: clonedSlideData,
  };

  store.slides.splice(slideIndex + 1, 0, newSlide);
  store.anyUnsavedChanges = true;
  store.addToHistory();
  // Scroll by height of next slide minus 20px
  scrollContainerBySlideHeight(
    "form-builder",
    "form-builder-container",
    ".survey-form-container",
    slideIndex + 1,
    20
  );
};

const hasLogicConflict = (slideIndex) => {
  if (
    store.slides[slideIndex].logic &&
    Object.keys(store.slides[slideIndex].logic).length > 1
  ) {
    return true;
  }
  return false;
};

const applyInlineStyle = () => {
  return {
    marginBottom: store.formStyle.fieldSpacing + "px",
  };
};

/**
 * Handle contact association settings save
 */
const saveContactAssociationSettings = (settings) => {
  store.contactAssociationSettings = settings;
  store.anyUnsavedChanges = true;
  store.showContactAssociationModal = false;
};

/**
 * Handle contact association settings cancel
 */
const cancelContactAssociationSettings = () => {
  store.showContactAssociationModal = false;
};

const shouldRemoveExtraPadding = computed(() => {
  const padding =
    store.deviceMode === "mobile"
      ? store.formStyle.mobilePadding
      : store.formStyle.padding;
  return padding?.extraPaddingComputingProcessed === true;
});

// Survey Image Layout Computed Properties
const getCurrentSlideImageSettings = computed(() => {
  return (slideIndex: number) => {
    const slideId = store.slides[slideIndex]?.id;

    // Ensure slideSettings exists
    if (!store.surveyImageSettings.slideSettings) {
      store.surveyImageSettings.slideSettings = {};
    }

    const slideSettings = store.surveyImageSettings.slideSettings[slideId];

    // Check if this slide uses survey default or independent mode
    const slideMode = slideSettings?.mode || SURVEY_MODE.SURVEY_DEFAULT;

    if (slideMode === SURVEY_MODE.SURVEY_DEFAULT) {
      return store.surveyImageSettings.surveyDefault;
    }

    // Independent mode - use slide-specific settings with defaults
    return {
      mode: SURVEY_MODE.INDEPENDENT,
      showImage: false,
      desktopImageLayout: IMAGE_LAYOUT.TOP_FIXED,
      mobileImageLayout: IMAGE_LAYOUT.HEADER,
      headerImageSrc: "",
      mobileHeaderImageSrc: "",
      mobileHeaderImageSrcDeleted: false,
      imageFieldSpacing: 24,
      imageCornerRadius: 0,
      ...slideSettings,
    };
  };
});

const getCurrentSlideImageLayout = computed(() => {
  return (slideIndex: number) => {
    const settings = getCurrentSlideImageSettings.value(slideIndex);

    if (!settings.showImage) {
      return null;
    }

    return store.deviceMode === "mobile"
      ? settings.mobileImageLayout || IMAGE_LAYOUT.HEADER
      : settings.desktopImageLayout || IMAGE_LAYOUT.TOP_FIXED;
  };
});

const getCurrentSlideImageUrl = computed(() => {
  return (slideIndex: number) => {
    const settings = getCurrentSlideImageSettings.value(slideIndex);
    if (store.deviceMode === "mobile") {
      const mobileUrl = (settings.mobileHeaderImageSrc || "").trim();
      if (mobileUrl.length > 0) {
        return settings.mobileHeaderImageSrc;
      }
      if ((settings as any).mobileHeaderImageSrcDeleted) {
        return "";
      }
      return settings.headerImageSrc;
    }
    return settings.headerImageSrc;
  };
});

// Get focus point for the current slide's image (desktop only)
const getCurrentSlideImageFocusPoint = computed(() => {
  return (slideIndex: number) => {
    const settings = getCurrentSlideImageSettings.value(slideIndex);
    const defaultFocusPoint = { x: 50, y: 50 };
    return (settings as any).imageFocusPoint || defaultFocusPoint;
  };
});

// Get background position string based on focus point for the slide
const getSlideImageBackgroundPosition = computed(() => {
  return (slideIndex: number) => {
    const focusPoint = getCurrentSlideImageFocusPoint.value(slideIndex);
    return `${focusPoint.x}% ${focusPoint.y}%`;
  };
});

const shouldShowImageForSlide = computed(() => {
  return (slideIndex: number) => {
    const settings = getCurrentSlideImageSettings.value(slideIndex);
    const hasMobileImage =
      (settings.mobileHeaderImageSrc || "").trim().length > 0;
    const hasDesktopImage = (settings.headerImageSrc || "").trim().length > 0;
    const mobileHeaderImageDeleted = Boolean(
      (settings as any).mobileHeaderImageSrcDeleted
    );
    const hasImage =
      store.deviceMode === "mobile"
        ? hasMobileImage || (!mobileHeaderImageDeleted && hasDesktopImage)
        : hasDesktopImage;
    return settings.showImage && hasImage;
  };
});

// Dynamic margins for left-fixed layout image-side
const getLeftFixedImageMargins = computed(() => {
  return (slideIndex: number) => {
    const padding =
      store.deviceMode === "mobile"
        ? store.formStyle.mobilePadding || store.formStyle.padding
        : store.formStyle.padding;

    const top = padding?.top || 0;
    const left = padding?.left || 0;

    return {
      marginTop: `-${top}px`,
      marginLeft: `-${left}px`,
    };
  };
});

// Dynamic margins for right-fixed layout image-side
const getRightFixedImageMargins = computed(() => {
  return (slideIndex: number) => {
    const padding =
      store.deviceMode === "mobile"
        ? store.formStyle.mobilePadding || store.formStyle.padding
        : store.formStyle.padding;

    const top = padding?.top || 0;
    const left = padding?.left || 0;

    return {
      marginTop: `-${top}px`,
      marginLeft: `${left}px`,
    };
  };
});

// Dynamic CSS class for image layout
const getImageLayoutClass = computed(() => {
  return (slideIndex: number) => {
    if (!shouldShowImageForSlide.value(slideIndex)) {
      return "";
    }

    const layout = getCurrentSlideImageLayout.value(slideIndex);
    if (!layout) {
      return "";
    }

    // Convert camelCase to kebab-case for CSS classes
    const layoutClass = layout.replace(/([A-Z])/g, "-$1").toLowerCase();

    return `image-layout-${layoutClass}`;
  };
});

// Enhanced form style for each slide - padding always stays on the-form-builder
const getEnhancedSlideFormStyle = computed(() => {
  return (slideIndex: number) => {
    const baseStyle = formStyle.value;
    const layout = getCurrentSlideImageLayout.value(slideIndex);
    const radius = store.formStyle.border.radius || 0;

    // Add background image for background layout
    if (
      layout === IMAGE_LAYOUT.BACKGROUND &&
      shouldShowImageForSlide.value(slideIndex)
    ) {
      return {
        ...baseStyle,
        backgroundImage: `url(${getCurrentSlideImageUrl.value(slideIndex)})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      };
    }

    // Base border radius: account for sticky footer and mobile progress bar
    const footerStyle =
      store.deviceMode === "mobile"
        ? store.mobileFooterStyle
        : store.footerStyle;
    const isStickyFooter = footerStyle?.stickyFooter;
    const hasMobileProgressBar =
      store.deviceMode === "mobile" &&
      store.mobileFooterStyle?.enableProgressBar &&
      store.slides.length > 1;

    let borderRadius: string;
    if (isStickyFooter) {
      borderRadius = hasMobileProgressBar
        ? "0 0 0 0"
        : `${radius}px ${radius}px 0 0`;
    } else {
      borderRadius = hasMobileProgressBar
        ? `0 0 ${radius}px ${radius}px`
        : `${radius}px`;
    }

    // Adjust border radius based on image layout (preserve sticky footer: no bottom corners)
    if (shouldShowImageForSlide.value(slideIndex)) {
      if (layout === IMAGE_LAYOUT.TOP_FIXED || layout === IMAGE_LAYOUT.HEADER) {
        // Image is on top, so form gets bottom corners rounded; sticky footer → bottom = 0
        borderRadius = isStickyFooter
          ? "0 0 0 0"
          : `0 0 ${radius}px ${radius}px`;
      } else if (layout === IMAGE_LAYOUT.LEFT_FIXED) {
        // Image on left: form gets right corners rounded; sticky footer → bottom-right = 0
        borderRadius = isStickyFooter
          ? `${radius}px ${radius}px 0 0`
          : `${radius}px`;
      } else if (layout === IMAGE_LAYOUT.RIGHT_FIXED) {
        // Image on right: form gets left corners rounded; sticky footer → bottom-left = 0
        borderRadius = isStickyFooter
          ? `${radius}px ${radius}px 0 0`
          : `${radius}px`;
      }
    }

    // For leftFixed/rightFixed layouts, remove left/right padding from the container
    // as it will be applied to form-side only
    let adjustedPadding = baseStyle.padding;
    if (
      shouldShowImageForSlide.value(slideIndex) &&
      store.deviceMode !== "mobile" &&
      (layout === IMAGE_LAYOUT.LEFT_FIXED ||
        layout === IMAGE_LAYOUT.RIGHT_FIXED)
    ) {
      const style = store.formStyle;
      const padding =
        store.deviceMode === "mobile"
          ? style?.mobilePadding || style?.padding
          : style?.padding;

      if (layout === IMAGE_LAYOUT.LEFT_FIXED) {
        // Remove left padding from container, keep others
        adjustedPadding = `${padding?.top || 0}px ${padding?.right || 0}px ${
          padding?.bottom || 0
        }px 0px`;
      } else if (layout === IMAGE_LAYOUT.RIGHT_FIXED) {
        // Remove right padding from container, keep others
        adjustedPadding = `${padding?.top || 0}px 0px ${
          padding?.bottom || 0
        }px ${padding?.left || 0}px`;
      }
    }

    return {
      ...baseStyle,
      padding: adjustedPadding,
      borderRadius,
    };
  };
});

// Form side style - width, spacing, and padding for fixed layouts
// The spacing is cut from form side to keep image side at exact 40%
const getFormSideStyle = computed(() => {
  return (slideIndex: number) => {
    const layout = getCurrentSlideImageLayout.value(slideIndex);

    // In mobile mode, always use 100% width
    if (store.deviceMode === "mobile") {
      return { width: "100%" };
    }

    // For background, topFixed, or no layout, use 100%
    if (
      layout === IMAGE_LAYOUT.BACKGROUND ||
      layout === IMAGE_LAYOUT.TOP_FIXED ||
      !layout
    ) {
      return { width: "100%" };
    }

    const style = store.formStyle;
    const padding =
      store.deviceMode === "mobile"
        ? style?.mobilePadding || style?.padding
        : style?.padding;

    // For leftFixed layout, apply left padding to form content (no gap spacing)
    if (
      shouldShowImageForSlide.value(slideIndex) &&
      layout === IMAGE_LAYOUT.LEFT_FIXED
    ) {
      return {
        width: "60%",
        paddingLeft: `${padding?.left || 0}px`,
      };
    }

    // For rightFixed layout, apply right padding to form content (no gap spacing)
    if (
      shouldShowImageForSlide.value(slideIndex) &&
      layout === IMAGE_LAYOUT.RIGHT_FIXED
    ) {
      return {
        width: "60%",
        paddingRight: `${padding?.right || 0}px`,
      };
    }

    // For side layouts (leftCenter, rightCenter),
    // cut the spacing from form side so image side remains at 40%
    const settings = getCurrentSlideImageSettings.value(slideIndex);
    const spacing = settings.imageFieldSpacing ?? 24;
    return { width: `calc(60% - ${spacing}px)` };
  };
});

// Image and form gap spacing for each slide
// Image and form gap spacing (only for leftCenter and rightCenter layouts)
const getImageFormGap = computed(() => {
  return (slideIndex: number) => {
    const layout = getCurrentSlideImageLayout.value(slideIndex);
    const shouldShowGap =
      shouldShowImageForSlide.value(slideIndex) &&
      (
        [IMAGE_LAYOUT.LEFT_CENTER, IMAGE_LAYOUT.RIGHT_CENTER] as string[]
      ).includes(layout);

    if (!shouldShowGap) {
      return "0px";
    }

    const settings = getCurrentSlideImageSettings.value(slideIndex);
    return `${settings.imageFieldSpacing ?? 24}px`;
  };
});

// Image corner radius for each slide
const getImageCornerRadius = computed(() => {
  return (slideIndex: number) => {
    const layout = getCurrentSlideImageLayout.value(slideIndex);

    if (
      layout !== IMAGE_LAYOUT.LEFT_CENTER &&
      layout !== IMAGE_LAYOUT.RIGHT_CENTER
    ) {
      return "0px";
    }

    const settings = getCurrentSlideImageSettings.value(slideIndex);
    return `${settings.imageCornerRadius ?? 0}px`;
  };
});

// Image side width - always 40% for side layouts
const getImageSideWidth = computed(() => {
  return (slideIndex: number) => {
    const layout = getCurrentSlideImageLayout.value(slideIndex);
    const shouldUseFixedWidth =
      shouldShowImageForSlide.value(slideIndex) &&
      (
        [
          IMAGE_LAYOUT.LEFT_CENTER,
          IMAGE_LAYOUT.RIGHT_CENTER,
          IMAGE_LAYOUT.LEFT_FIXED,
          IMAGE_LAYOUT.RIGHT_FIXED,
        ] as string[]
      ).includes(layout) &&
      store.deviceMode !== "mobile";

    if (!shouldUseFixedWidth) {
      return "40%";
    }

    return "40%";
  };
});

// Top image border radius for each slide (for topFixed/header layouts)
const getTopImageBorderRadius = computed(() => {
  return (slideIndex: number) => {
    const layout = getCurrentSlideImageLayout.value(slideIndex);
    const radius = store.formStyle.border.radius || 0;
    if (layout === IMAGE_LAYOUT.TOP_FIXED || layout === IMAGE_LAYOUT.HEADER) {
      return `${radius}px ${radius}px 0 0`;
    }
    return "0";
  };
});

// Side image border radius for each slide (for leftFixed/rightFixed layouts)
const getSideImageBorderRadius = computed(() => {
  return (slideIndex: number) => {
    const layout = getCurrentSlideImageLayout.value(slideIndex);
    const radius = store.formStyle.border.radius || 0;
    const footerStyle =
      store.deviceMode === "mobile"
        ? store.mobileFooterStyle
        : store.footerStyle;
    const isStickyFooter = footerStyle?.stickyFooter;

    if (layout === IMAGE_LAYOUT.LEFT_FIXED) {
      // Image on left: left corners rounded; when sticky footer, bottom-left = 0
      return isStickyFooter
        ? `${radius}px 0 0 0`
        : `${radius}px 0 0 ${radius}px`;
    }
    if (layout === IMAGE_LAYOUT.RIGHT_FIXED) {
      // Image on right: right corners rounded; when sticky footer, bottom-right = 0
      return isStickyFooter
        ? `0 ${radius}px 0 0`
        : `0 ${radius}px ${radius}px 0`;
    }
    return "0";
  };
});

// Image side negative margins to counteract form padding (for leftFixed/rightFixed)
const getImageSideMargins = computed(() => {
  return (slideIndex: number) => {
    const layout = getCurrentSlideImageLayout.value(slideIndex);
    const shouldApplyMargins =
      shouldShowImageForSlide.value(slideIndex) &&
      (
        [IMAGE_LAYOUT.LEFT_FIXED, IMAGE_LAYOUT.RIGHT_FIXED] as string[]
      ).includes(layout) &&
      store.deviceMode !== "mobile";

    if (!shouldApplyMargins) {
      return {};
    }

    const style = store.formStyle;
    const padding =
      store.deviceMode === "mobile"
        ? style?.mobilePadding || style?.padding
        : style?.padding;

    const topPadding = padding?.top || 0;
    const bottomPadding = padding?.bottom || 0;

    return {
      marginTop: `-${topPadding}px`,
      marginBottom: `-${bottomPadding}px`,
    };
  };
});
</script>

<template>
  <section
    class="hl_wrapper d-flex survey-wrap-container"
    @click.prevent="clickedOutSideSlide"
  >
    <section
      id="form-builder"
      class="hl_wrapper--inner form-builder"
      :style="{
        backgroundImage: `url(${builderCanvasBackgroundUrl})`,
        backgroundSize: 'cover',
      }"
      @mousedown.capture="preserveBuilderScroll"
      @focusin.capture="preserveBuilderScroll"
    >
      <section
        class="hl_form-builder--main"
        :style="{
          marginTop: containerMarginTop,
          marginBottom: containerMarginBottom,
        }"
      >
        <form id="_builder-form" name="builder-form" class="builder-preview">
          <div class="form-child-wrapper">
            <div class="smooth-dnd-container vertical">
              <VueDraggableNext
                id="form-builder-container"
                :list="store.slides"
                :sort="true"
                group="slide"
                :animation="300"
                @change="slideChange"
              >
                <div
                  v-for="(itemSlide, indexSlide) in store.slides"
                  :key="indexSlide"
                  class="smooth-dnd-draggable-wrapper"
                >
                  <div
                    class="survey-form-container"
                    :style="{
                      paddingBottom:
                        indexSlide === store.slides.length - 1 ? '200px' : 0,
                    }"
                  >
                    <div>
                      <div
                        class="builder-form-name mb-1 flex h-10 items-center justify-between justify-items-center text-center"
                        :style="{ maxWidth: formWidth + 'px' }"
                      >
                        <div class="flex items-center text-sm font-semibold">
                          {{ itemSlide?.slideName }}
                          <UITooltip
                            v-if="hasLogicConflict(indexSlide)"
                            :style="{ maxWidth: '300px' }"
                            trigger="hover"
                            placement="top"
                          >
                            <template #trigger>
                              <AlertCircleIcon
                                class="ml-1 h-4 w-4 text-red-400"
                              ></AlertCircleIcon>
                            </template>

                            {{ t("survey.slideConflictAlertMessage") }}
                          </UITooltip>
                        </div>
                        <div class="ml-2 flex items-center gap-2">
                          <UITooltip trigger="hover" placement="top">
                            <template #trigger>
                              <Settings02Icon
                                class="focus_outline_none h-5 w-5 cursor-pointer text-gray-900 ring-0 hover:text-gray-700 focus:outline-none focus:ring-0 active:outline-none"
                                @mousedown.stop
                                @click.stop="openSlideSettings(indexSlide)"
                              />
                            </template>
                            {{ t("survey.settings") }}
                          </UITooltip>
                          <UITooltip trigger="hover" placement="top">
                            <template #trigger>
                              <Copy01Icon
                                class="focus_outline_none h-5 w-5 cursor-pointer text-gray-900 ring-0 hover:text-gray-700 focus:outline-none focus:ring-0 active:outline-none"
                                @mousedown.stop
                                @click.stop="cloneSlide(indexSlide)"
                              />
                            </template>
                            {{ t("survey.cloneSlide") }}
                          </UITooltip>
                          <UITooltip trigger="hover" placement="top">
                            <template #trigger>
                              <Trash04Icon
                                class="focus_outline_none h-5 w-5 cursor-pointer text-gray-900 ring-0 hover:text-gray-700 focus:outline-none focus:ring-0 active:outline-none"
                                :class="{
                                  'cursor-not-allowed opacity-50':
                                    store.slides.length === 1,
                                }"
                                @mousedown.stop
                                @click.stop="
                                  store.slides.length === 1
                                    ? null
                                    : removeSlide(indexSlide)
                                "
                              />
                            </template>
                            {{ t("survey.delete") }}
                          </UITooltip>
                        </div>
                      </div>
                      <!-- Mobile Progress Bar -->
                      <div
                        v-if="
                          store.deviceMode === 'mobile' &&
                          store.mobileFooterStyle?.enableProgressBar
                        "
                        class="mobile-progress-bar-overlay"
                        :style="{
                          backgroundColor: `#${store.mobileFooterStyle.progressBarStyle.inactiveFillColor}`,
                          maxWidth: formWidth + 'px',
                        }"
                      >
                        <div
                          class="mobile-progress-fill"
                          :style="{
                            width: progressBarWidth(indexSlide),
                            backgroundColor: `#${store.mobileFooterStyle.progressBarStyle.completeFillColor}`,
                          }"
                        ></div>
                      </div>
                      <!-- Survey Image Layout System - Top Fixed / Header (Outside slide content) -->
                      <div
                        v-if="
                          shouldShowImageForSlide(indexSlide) &&
                          (getCurrentSlideImageLayout(indexSlide) ===
                            IMAGE_LAYOUT.TOP_FIXED ||
                            getCurrentSlideImageLayout(indexSlide) ===
                              IMAGE_LAYOUT.HEADER)
                        "
                        class="ghl-header-banner w-full max-w-full"
                        :style="{
                          maxWidth: formWidth + 'px',
                        }"
                      >
                        <HLImage
                          :url="getCurrentSlideImageUrl(indexSlide)"
                          :width="store.formStyle.width"
                          :alt="t('common.bannerAlt')"
                          :style="{
                            borderRadius: getTopImageBorderRadius(indexSlide),
                          }"
                        ></HLImage>
                      </div>
                      <div
                        id="the-form-builder"
                        class="form-element form-builder--wrap"
                        :class="[
                          store.formAction.fieldSettingEnable
                            ? 'one-question-height'
                            : 'normal-height',
                          !itemSlide?.slideData?.length && 'zero_screen_height',
                        ]"
                        :style="getEnhancedSlideFormStyle(indexSlide)"
                      >
                        <!-- Image Layout Container -->
                        <div
                          v-if="shouldShowImageForSlide(indexSlide)"
                          :class="[
                            'flex items-center',
                            getImageLayoutClass(indexSlide),
                          ]"
                          :style="{ gap: getImageFormGap(indexSlide) }"
                        >
                          <div
                            v-if="
                              store.deviceMode !== 'mobile' &&
                              getCurrentSlideImageLayout(indexSlide) !==
                                IMAGE_LAYOUT.TOP_FIXED &&
                              getCurrentSlideImageLayout(indexSlide) !==
                                IMAGE_LAYOUT.BACKGROUND
                            "
                            class="image-side"
                            :style="
                              getImageLayoutClass(indexSlide) ===
                                'image-layout-left-fixed' ||
                              getImageLayoutClass(indexSlide) ===
                                'image-layout-right-fixed'
                                ? {
                                    width: getImageSideWidth(indexSlide),
                                    backgroundImage: `url(${getCurrentSlideImageUrl(
                                      indexSlide
                                    )})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition:
                                      getSlideImageBackgroundPosition(
                                        indexSlide
                                      ),
                                    backgroundRepeat: 'no-repeat',
                                    height: '-webkit-fill-available',
                                    borderRadius:
                                      getSideImageBorderRadius(indexSlide),
                                    ...getImageSideMargins(indexSlide),
                                  }
                                : {
                                    width: getImageSideWidth(indexSlide),
                                  }
                            "
                          >
                            <HLImage
                              v-if="
                                !(
                                  getImageLayoutClass(indexSlide) ===
                                    'image-layout-left-fixed' ||
                                  getImageLayoutClass(indexSlide) ===
                                    'image-layout-right-fixed'
                                ) &&
                                getCurrentSlideImageLayout(indexSlide) !==
                                  IMAGE_LAYOUT.TOP_FIXED &&
                                getCurrentSlideImageLayout(indexSlide) !==
                                  IMAGE_LAYOUT.BACKGROUND
                              "
                              :url="getCurrentSlideImageUrl(indexSlide)"
                              :alt="t('common.bannerAlt')"
                              class="h-auto w-full"
                              :style="{
                                borderRadius: getImageCornerRadius(indexSlide),
                              }"
                            />
                          </div>
                          <div
                            class="form-side"
                            :style="getFormSideStyle(indexSlide)"
                          >
                            <div
                              :class="`drop ${
                                shouldRemoveExtraPadding
                                  ? ''
                                  : 'builder-padding-remove'
                              } ${
                                store.formAction.fieldSettingEnable
                                  ? 'one-question-form-builder-drop'
                                  : 'form-builder-drop'
                              }  ${
                                !itemSlide?.slideData?.length &&
                                'zero_screen_height'
                              }`"
                            >
                              <VueDraggableNext
                                :model-value="itemSlide.slideData"
                                :class="[
                                  store.builderConfig.inlineForm
                                    ? 'smooth-dnd-container vertical row'
                                    : 'smooth-dnd-container vertical',
                                  store.formAction.fieldSettingEnable
                                    ? 'builder_one_question_container'
                                    : 'builder_sub_container',
                                  !itemSlide?.slideData?.length &&
                                    'zero_screen_height',
                                ]"
                                group="elements"
                                :sort="true"
                                :animation="300"
                                handle=".field-draggable-area"
                                ghost-class="field-drag-ghost"
                                @start="
                                  (e) => handleSurveyDragStart(e, indexSlide)
                                "
                                @end="handleSurveyDragEnd"
                                @change="
                                  (e) =>
                                    handleSurveyElementChange(e, indexSlide)
                                "
                              >
                                <DefaultScreen
                                  v-if="!itemSlide?.slideData?.length"
                                  :add-slide-text="t('survey.addSlideText')"
                                  :page-index="indexSlide"
                                />
                                <div
                                  v-for="(
                                    element, index
                                  ) in itemSlide.slideData"
                                  v-else
                                  :key="
                                    getElementDOMUniqueId(
                                      'survey-element',
                                      element,
                                      index,
                                      indexSlide
                                    )
                                  "
                                  class="smooth-dnd-draggable-wrapper element-box menu-field-wrap col-12 form-field-wrapper"
                                  :style="applyInlineStyle()"
                                  @click="
                                    (e) =>
                                      clickedOnElement(
                                        e,
                                        element,
                                        index,
                                        indexSlide
                                      )
                                  "
                                >
                                  <div
                                    :class="{
                                      active: isActiveElement(
                                        index,
                                        indexSlide
                                      ),
                                    }"
                                    class="menu-item"
                                  >
                                    <StandardFieldPreview
                                      v-if="
                                        !(
                                          element.id !== undefined ||
                                          element?.custom
                                        )
                                      "
                                      :key="
                                        getElementDOMUniqueId(
                                          'std-field',
                                          element,
                                          index,
                                          indexSlide
                                        )
                                      "
                                      :element="element"
                                      :index="index"
                                      :slide-index="indexSlide"
                                      :current-element="currentElement"
                                      :field-style="fieldStyle"
                                      :form-label-visible="
                                        store.builderConfig.formLabelVisible
                                      "
                                      @update:label="
                                        (label) =>
                                          updateLabel(
                                            label,
                                            element.hiddenFieldQueryKey
                                          )
                                      "
                                      @update:placeholder="
                                        (placeholder) =>
                                          updatePlaceholder(
                                            placeholder,
                                            element.hiddenFieldQueryKey
                                          )
                                      "
                                      @update:short-label="
                                        (shortLabel) =>
                                          updateShortLabel(
                                            shortLabel,
                                            element.hiddenFieldQueryKey
                                          )
                                      "
                                      :media-center-sub-category="
                                        MediaCenterSubCategory.SURVEY
                                      "
                                    ></StandardFieldPreview>
                                    <CustomFieldsPreview
                                      v-if="
                                        element.id !== undefined ||
                                        element?.custom
                                      "
                                      :key="
                                        getElementDOMUniqueId(
                                          'custom-field',
                                          element,
                                          index,
                                          indexSlide
                                        )
                                      "
                                      :element="element"
                                      :index="index"
                                      :slide-index="indexSlide"
                                      :current-element="currentElement"
                                      :field-style="fieldStyle"
                                      :form-label-visible="
                                        store.builderConfig.formLabelVisible
                                      "
                                      :is-form="false"
                                      @update:label="
                                        (label) =>
                                          updateLabel(
                                            label,
                                            element.hiddenFieldQueryKey
                                          )
                                      "
                                      @update:placeholder="
                                        (placeholder) =>
                                          updatePlaceholder(
                                            placeholder,
                                            element.hiddenFieldQueryKey
                                          )
                                      "
                                      @update:short-label="
                                        (shortLabel) =>
                                          updateShortLabel(
                                            shortLabel,
                                            element.hiddenFieldQueryKey
                                          )
                                      "
                                      @update:lowest-rating="
                                        (lowestRating) =>
                                          updateLowestRating(
                                            lowestRating,
                                            element.hiddenFieldQueryKey
                                          )
                                      "
                                      @update:highest-rating="
                                        (highestRating) =>
                                          updateHighestRating(
                                            highestRating,
                                            element.hiddenFieldQueryKey
                                          )
                                      "
                                    ></CustomFieldsPreview>
                                  </div>
                                </div>
                              </VueDraggableNext>
                              <BuilderBranding
                                :is-branding-active="store.formStyle.acBranding"
                                :company="store.company"
                              />
                            </div>
                          </div>
                        </div>

                        <!-- Fallback for when no image layout is active -->
                        <div v-else>
                          <div
                            :class="`drop ${
                              shouldRemoveExtraPadding
                                ? ''
                                : 'builder-padding-remove'
                            } ${
                              store.formAction.fieldSettingEnable
                                ? 'one-question-form-builder-drop'
                                : 'form-builder-drop'
                            }
                                 ${
                                   !itemSlide?.slideData?.length &&
                                   'zero_screen_height'
                                 }`"
                          >
                            <VueDraggableNext
                              :model-value="itemSlide.slideData"
                              :class="[
                                store.builderConfig.inlineForm
                                  ? 'smooth-dnd-container vertical row'
                                  : 'smooth-dnd-container vertical',
                                store.formAction.fieldSettingEnable
                                  ? 'builder_one_question_container'
                                  : 'builder_sub_container',
                                !itemSlide?.slideData?.length &&
                                  'zero_screen_height',
                              ]"
                              group="elements"
                              :sort="true"
                              @change="
                                (e) => handleSurveyElementChange(e, indexSlide)
                              "
                              :animation="300"
                              handle=".field-draggable-area"
                              ghost-class="field-drag-ghost"
                              @start="
                                (e) => handleSurveyDragStart(e, indexSlide)
                              "
                              @end="handleSurveyDragEnd"
                            >
                              <DefaultScreen
                                v-if="!itemSlide?.slideData?.length"
                                :add-slide-text="t('survey.addSlideText')"
                                :page-index="indexSlide"
                              />
                              <div
                                v-else
                                v-for="(element, index) in itemSlide.slideData"
                                :key="
                                  getElementDOMUniqueId(
                                    'survey-element',
                                    element,
                                    index,
                                    indexSlide
                                  )
                                "
                                class="smooth-dnd-draggable-wrapper element-box menu-field-wrap col-12 form-field-wrapper"
                                :style="applyInlineStyle()"
                                @click="
                                  (e) =>
                                    clickedOnElement(
                                      e,
                                      element,
                                      index,
                                      indexSlide
                                    )
                                "
                              >
                                <div
                                  :class="{
                                    active: isActiveElement(index, indexSlide),
                                  }"
                                  class="menu-item"
                                >
                                  <StandardFieldPreview
                                    v-if="
                                      !(
                                        element.id !== undefined ||
                                        element?.custom
                                      )
                                    "
                                    :key="
                                      getElementDOMUniqueId(
                                        'std-field',
                                        element,
                                        index,
                                        indexSlide
                                      )
                                    "
                                    :element="element"
                                    :index="index"
                                    :slide-index="indexSlide"
                                    :current-element="currentElement"
                                    :field-style="fieldStyle"
                                    :form-label-visible="
                                      store.builderConfig.formLabelVisible
                                    "
                                    @update:label="
                                      (label) =>
                                        updateLabel(
                                          label,
                                          element.hiddenFieldQueryKey
                                        )
                                    "
                                    @update:placeholder="
                                      (placeholder) =>
                                        updatePlaceholder(
                                          placeholder,
                                          element.hiddenFieldQueryKey
                                        )
                                    "
                                    @update:short-label="
                                      (shortLabel) =>
                                        updateShortLabel(
                                          shortLabel,
                                          element.hiddenFieldQueryKey
                                        )
                                    "
                                    :media-center-sub-category="
                                      MediaCenterSubCategory.SURVEY
                                    "
                                  ></StandardFieldPreview>
                                  <CustomFieldsPreview
                                    v-if="
                                      element.id !== undefined ||
                                      element?.custom
                                    "
                                    :key="
                                      getElementDOMUniqueId(
                                        'custom-field',
                                        element,
                                        index,
                                        indexSlide
                                      )
                                    "
                                    :element="element"
                                    :index="index"
                                    :slide-index="indexSlide"
                                    :current-element="currentElement"
                                    :field-style="fieldStyle"
                                    :form-label-visible="
                                      store.builderConfig.formLabelVisible
                                    "
                                    :is-form="false"
                                    @update:label="
                                      (label) =>
                                        updateLabel(
                                          label,
                                          element.hiddenFieldQueryKey
                                        )
                                    "
                                    @update:placeholder="
                                      (placeholder) =>
                                        updatePlaceholder(
                                          placeholder,
                                          element.hiddenFieldQueryKey
                                        )
                                    "
                                    @update:short-label="
                                      (shortLabel) =>
                                        updateShortLabel(
                                          shortLabel,
                                          element.hiddenFieldQueryKey
                                        )
                                    "
                                    @update:lowest-rating="
                                      (lowestRating) =>
                                        updateLowestRating(
                                          lowestRating,
                                          element.hiddenFieldQueryKey
                                        )
                                    "
                                    @update:highest-rating="
                                      (highestRating) =>
                                        updateHighestRating(
                                          highestRating,
                                          element.hiddenFieldQueryKey
                                        )
                                    "
                                  ></CustomFieldsPreview>
                                </div>
                              </div>
                            </VueDraggableNext>
                            <BuilderBranding
                              :is-branding-active="store.formStyle.acBranding"
                              :company="store.company"
                            />
                          </div>
                        </div>
                      </div>
                      <div class="flex justify-center">
                        <Footer
                          v-if="store.footerStyle.stickyFooter"
                          :current-slide-index="indexSlide"
                          :is-last-slide-index="
                            indexSlide === store.slides.length - 1
                          "
                          :style="{ maxWidth: formWidth + 'px' }"
                        />
                        <Footer
                          v-if="
                            !store.footerStyle.stickyFooter &&
                            store.deviceMode === 'mobile'
                          "
                          :current-slide-index="indexSlide"
                          :is-last-slide-index="
                            indexSlide === store.slides.length - 1
                          "
                          :style="{ maxWidth: formWidth + 'px' }"
                        />
                      </div>

                      <section class="flex justify-center">
                        <div
                          :style="{ maxWidth: formWidth + 'px' }"
                          class="w-full"
                        >
                          <Divider
                            :add-slide-text="t('survey.addSlide')"
                            @add-slide="() => addSlideBelow(indexSlide)"
                          />
                        </div>
                      </section>
                    </div>
                  </div>
                </div>
              </VueDraggableNext>
            </div>
          </div>
        </form>
      </section>
    </section>
    <Footer
      v-if="!store.footerStyle.stickyFooter && store.deviceMode === 'desktop'"
    />
  </section>
  <AlertModal
    v-if="openAlertModal"
    :title="slideDeleteTitle"
    :description="slideDeleteMessage"
    modal-name="slideDelete"
    :show-modal-popup="openAlertModal"
    :positive-text="slideDeletePositiveText"
    :negative-text="slideDeleteNegativeText"
    header-type="error"
    footer-type="primary"
    @pop:change="popModalChange"
    @pop:positive="feedback"
    @pop:negative="feedback"
  ></AlertModal>
  <ContactAssociationSettingsModal
    :show="store.showContactAssociationModal"
    :object-model="store.currentCustomObjectModel"
    :with-object-model="store.currentWithObjectModel"
    @update:success="saveContactAssociationSettings"
    @update:cancel="cancelContactAssociationSettings"
  />
  <MixedCustomObjectErrorModal
    :show="store.showMixedCustomObjectErrorModal"
    :existing-object-name="store.mixedCustomObjectErrorInfo.existingObjectName"
    :attempted-object-name="
      store.mixedCustomObjectErrorInfo.attemptedObjectName
    "
    @update:close="store.closeMixedCustomObjectErrorModal()"
  />
</template>

<style scoped>
.settings {
  position: absolute;
  top: -40px;
  left: 50%;
  transform: translate(-50%, 0);
  padding: 5px;
  border-radius: 5px;
  background-color: #007bff;
  color: #fff;
  border-color: #007bff;
  z-index: 100;
  position: absolute;
  margin-bottom: 10px;
}

.form-control {
  border-radius: 0.3125rem;
  padding: 15px 20px;
  font-size: 0.875rem;
}
.form-control:disabled,
.form-control [readonly] {
  background-color: #e9ecef;
  opacity: 1;
}

#settings {
  font-size: 10px;
  height: 30px;
}

.builder-form-name {
  margin: 0 auto;
}

.icon-settings {
  display: flex;
  align-items: center;
}

.icon-settings svg {
  padding-right: 5px;
}

/* Force 20px action icon size in header (overrides h-4 emitted classes) */
.builder-form-name svg {
  width: 20px !important;
  height: 20px !important;
}
/* Scoped deep selector to affect child component SVGs */
.builder-form-name :deep(svg) {
  width: 20px !important;
  height: 20px !important;
}

.builder-preview {
  margin: 0 auto;
  width: 100%;
}

.form-child-wrapper {
  margin: 0 auto;
}

#form-builder {
  background-color: #f3f4f6;
  margin-bottom: 0px;
  overflow: auto;
  margin: 0 auto;
  height: calc(100vh - 52px);
  width: 100%;
}

.add_new_slides {
  text-align: center;
  margin-bottom: 20px;
}

.builder-item {
  cursor: pointer;
  width: 100%;
  background: grey;
  margin: 0.5rem 0;
  min-height: 3rem;
}

.builder_one_question_container {
  min-height: 140px;
}
#_builder-form .active {
  background: transparent;
  cursor: move;
}

#_builder-form .main_active {
  border: 1px solid #168bf5;
}

.form-builder--wrap {
  margin: 0 auto;
  border-radius: 4px;
  border: 1px dashed #cde0ec;
  background-color: #fff;
  position: relative;
}
.one-question-height {
  min-height: 140px;
}
.survey-wrap-container .survey-form-container {
  margin: 0 auto;
  padding-top: 10px;
  padding-bottom: 10px;
  position: relative;
}

.form-builder--item {
  margin-bottom: 15px;
  border-radius: 4px;
  border: 2px solid transparent;
  -webkit-transition: border 0.2s ease-in-out;
  -o-transition: border 0.2s ease-in-out;
  transition: border 0.2s ease-in-out;
  position: relative;
}

.builder-inline
  .smooth-dnd-draggable-wrapper.col-md-6
  .f-even
  .form-builder--item {
  padding-left: 4px !important;
}

.builder-inline
  .smooth-dnd-draggable-wrapper.col-md-6
  .f-odd
  .form-builder--item {
  padding-left: 10px;
  padding-right: 4px;
}

.builder-inline .smooth-dnd-draggable-wrapper.col-12 .form-builder--item {
  padding-left: 10px;
  padding-right: 4px;
}

.form-builder--item h1,
.form-builder--item h2,
.form-builder--item h3,
.form-builder--item h4,
.form-builder--item h5,
.form-builder--item h6 {
  margin-bottom: 0;
}

.form-builder--item label {
  font-size: 0.875rem;
  color: #2a3135;
}

.button-element {
  min-width: 85px;
}

.menu-item {
  position: relative;
}

.menu-field-wrap {
  position: relative;
}

#the-form-builder {
  margin: 0 auto;
}

.form-builder-drop {
  min-height: 400px;
  display: flex;
  flex-direction: column;
}

.builder_sub_container {
  flex: 1;
  min-height: 100%;
}

.zero_screen_height {
  min-height: auto;
}
.one-question-form-builder-drop {
  min-height: 140px;
  padding: 0 15px;
}
.builder-padding-remove {
  padding: 30px 0;
}

.add-dropzone-builder {
  background: transparent;
  width: 100%;
  height: 86px;
  border-width: 1;
  border-style: solid;
  border-color: #e5e7eb;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #6c757d !important;
}

.add-dropzone-builder p {
  margin-left: 5px !important;
}

#form-builder-container {
  height: 100%;
  min-height: 340px;
}

.slide_settings {
  position: absolute;
  right: 130px;
  top: 10px;
}

.survey-form-container a.close-icon-survey {
  position: absolute;
  right: 0px;
  top: 0px;
  -webkit-transition: all 0.5s ease-in;
  transition: all 0.5s ease-in;
  font-size: 10px;
  /* padding: 5px 7px; */
  background: #f5f5f5;
  color: #2a3135;
  border-radius: 50%;
  height: 20px;
  cursor: pointer;
}
.focus_outline_none:focus {
  outline: none;
}
.ghl-header-banner {
  margin: 0 auto;
}

.element-box {
  display: flex;
  flex-flow: column;
  height: 100%;
}

.smooth-dnd-draggable-wrapper {
  cursor: move;
}

.field-drag-ghost {
  opacity: 1 !important;
  background-color: #f2f4f7 !important;
  border-radius: 4px;
  min-height: 60px;
  position: relative;
}

.field-drag-ghost * {
  visibility: hidden !important;
}

.header-image-container {
  position: relative;
  margin: 0 auto;
}

.mobile-progress-bar-overlay {
  position: relative;
  height: 8px;
  border-radius: 4px;
  z-index: 20;
  margin: 0 auto;
}

.mobile-progress-fill {
  height: 100%;
  border-radius: 4px 0 0 0;
  transition: width 0.3s ease-in-out;
}
#_builder-form :deep(p) {
  margin-bottom: 0;
}
/* Survey Image Layout Styles */
.survey-image-layout-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  justify-content: center;
  align-items: center;
}

.image-layout-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  justify-content: center;
  align-items: center;
}

.image-layout-top-fixed,
.image-layout-header {
  flex-direction: column;
  width: 100%;
}

.image-layout-left-center,
.image-layout-right-center {
  flex-direction: row;
  align-items: center;
  width: 100%;
  display: flex;
}

.image-layout-right-center {
  flex-direction: row-reverse;
}

.image-layout-left-center .image-side {
  overflow: hidden;
}

.image-layout-right-center .image-side {
  overflow: hidden;
}

.image-layout-left-fixed,
.image-layout-right-fixed {
  flex-direction: row;
  width: 100%;
  display: flex;
  min-height: 500px; /* Ensure height for fixed layouts */
}

.image-layout-right-fixed {
  flex-direction: row-reverse;
}

.image-side {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.form-side {
  min-height: 200px;
  overflow: visible;
  flex-shrink: 0;
}

/* Background and topFixed layouts - form takes full width */
.image-layout-background .form-side,
.image-layout-top-fixed .form-side {
  width: 100% !important;
}

/* For layouts with moved padding, ensure form-side handles padding properly */
/* Also vertically center form content for left/right image layouts */
.image-layout-left-center .form-side,
.image-layout-right-center .form-side,
.image-layout-left-fixed .form-side,
.image-layout-right-fixed .form-side {
  display: flex;
  flex-direction: column;
  justify-content: center; /* Stretch to fill parent height so justify-content works */
  box-sizing: border-box;
}

/* Remove min-height constraint on form-builder-drop for side image layouts to allow proper centering */
.image-layout-left-center .form-builder-drop,
.image-layout-right-center .form-builder-drop,
.image-layout-left-fixed .form-builder-drop,
.image-layout-right-fixed .form-builder-drop {
  min-height: auto;
}

/* Background layout specific styles */
#_builder-form[style*="background-image"] {
  position: relative;
  z-index: 1;
}

@media screen and (max-width: 650px) {
  .image-layout-left-center,
  .image-layout-right-center,
  .image-layout-left-fixed,
  .image-layout-right-fixed {
    flex-direction: column;
  }

  .image-side,
  .form-side {
    width: 100% !important;
  }

  .image-layout-left-fixed,
  .image-layout-right-fixed {
    min-height: auto;
  }
}
</style>
<style>
[id^="survey-dropdown-"] .n-dropdown-option-body__suffix {
  display: flex !important;
}
</style>
